<script setup lang="ts">
import SpinnerLoader from "@/components/Loaders/SpinnerLoader.vue";
import { getPhaseStatusColor } from "@/utils/status-color";
import { defineProps, ref, watch } from "vue";
import { useRoute } from "vue-router";
import ColoredDot from "@/components/Re-usable/ColoredDot.vue";
import { useTranslate } from "@/composables/useTranslate";

const route = useRoute();
const projectId = route.params.id;

type Props = {
  projectDetails?: any | null;
  phasePage?: any | null;
  appointmentPage?: any | null;
  isLoading?: boolean;
};
const props = defineProps<Props>();

const isAppointmentDropdown = ref(false);
const selectedAppointment = ref();

const isPhaseStepsDropdown = ref(false);
const selectedPhaseId = ref<number | null>(null);
const { t } = useTranslate();
const appointmentsDropdownOpened = ref([]);

// const isAppointmentItemActive = ref(false);

const toggleAppointmentsDropdown = (phase: any) => {
  if (appointmentsDropdownOpened.value.includes(phase)) {
    appointmentsDropdownOpened.value = appointmentsDropdownOpened.value.filter(
      (item: any) => item !== phase
    );
  } else {
    appointmentsDropdownOpened.value.push(phase);
  }

  if (selectedAppointment.value === phase) {
    isAppointmentDropdown.value = !isAppointmentDropdown.value;

    if (!isAppointmentDropdown.value) {
      selectedAppointment.value = null;
    }
  } else {
    selectedAppointment.value = phase;
    isAppointmentDropdown.value = true;
  }
};

watch(
  () => [route.params.phaseId, route.params.terminId],
  ([newPhaseId, newTerminId]) => {
    if (newPhaseId && props.phasePage?.phases) {
      const matchedPhase = props.phasePage.phases.find(
        (phase: any) => String(phase.phaseId) === String(newPhaseId)
      );
      if (matchedPhase) {
        selectedPhaseId.value = matchedPhase.phaseId;
        isPhaseStepsDropdown.value = true;
        if (!appointmentsDropdownOpened.value.includes(matchedPhase.phaseId)) {
          appointmentsDropdownOpened.value.push(matchedPhase.phaseId);
        }
      }
    } else {
      selectedPhaseId.value = null;
      isPhaseStepsDropdown.value = false;
    }

    if (newTerminId && props.appointmentPage) {
      for (const phase of props.appointmentPage) {
        const matchedTermin = phase.termins.find(
          (termin: any) => String(termin.id) === String(newTerminId)
        );
        if (matchedTermin) {
          if (!newPhaseId) {
            selectedPhaseId.value = phase.phase.id;
          }

          selectedAppointment.value = newTerminId;
          isAppointmentDropdown.value = true;
          if (
            !appointmentsDropdownOpened.value.includes(selectedPhaseId.value)
          ) {
            appointmentsDropdownOpened.value.push(selectedPhaseId.value);
          }
          break;
        }
      }
    } else {
      selectedAppointment.value = null;
      isAppointmentDropdown.value = false;
      appointmentsDropdownOpened.value =
        appointmentsDropdownOpened.value.filter(
          (item: any) => item !== newPhaseId
        );
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- Sidemenu main container -->
  <div
    v-if="props.projectDetails"
    class="w-[220px] h-full overflow-y-auto flex flex-col bg-[#E5E4E2]"
  >
    <!-- Phases -->
    <div
      v-for="(phase, index) in props.projectDetails"
      :key="phase.phaseId"
      class="w-full h-fit rounded-[4px] m-[2px] mt-[1px] ml-[1px] py-4 px-3 bg-[#E5E4E2]"
      :class="index === props.projectDetails.length - 1 ? 'h-full' : 'h-auto'"
    >
      <h4
        class="text-base leading-[26px] tracking-[0.01rem] uppercase font-medium text-[#51687A]"
      >
        {{ phase?.phase }}
      </h4>
      <!-- Menu items -->
      <div class="w-full flex flex-col items-start mt-2">
        <!-- Review -->
        <RouterLink
          :to="`/projects/${projectId}/phase/${phase.phaseId}`"
          class="w-fit cursor-pointer p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
        >
          <div class="w-full flex flex-wrap items-center gap-1">
            <img src="@/assets/eye-icon.svg" alt="Eye" />
            <h5 class="text-[#393840] font-medium text-sm tracking-[0.01rem]">
              {{ t("global.review") }}
            </h5>
            <div class="flex items-center">
              <span
                v-if="[3, 4, 5].includes(phase.phaseStatus?.id)"
                class="text-primary-text-color font-medium text-[13px] tracking-[0.01rem] px-[5px] py-[2px] rounded-primary-radius"
                :style="{
                  backgroundColor: `${getPhaseStatusColor(
                    phase.phaseStatus?.id
                  )}`,
                }"
                >{{ phase.phaseStatus.name }}</span
              >
              <ColoredDot
                v-else
                :status-name="phase.phaseStatus.name"
                :status-id="phase.phaseStatus.id"
                :is-termin="false"
              />
            </div>
          </div>
        </RouterLink>
        <!-- Appointment -->
        <div
          @click="toggleAppointmentsDropdown(phase)"
          class="w-full cursor-pointer p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
        >
          <div class="w-full flex items-center gap-1">
            <img src="@/assets/phase-menu-calendar-icon.svg" alt="Calendar" />
            <h5 class="text-[#393840] font-medium text-sm tracking-[0.01rem]">
              {{ t("global.termins") }}
            </h5>
          </div>
        </div>
        <ul v-if="selectedAppointment === phase">
          <li
            class="w-full flex flex-col gap-2 mb-[2px] pl-5 relative before:absolute before:w-[2px] before:bg-[#D3D1CD] before:h-full before:-ml-[6px]"
            v-for="termin in phase.termins"
            :key="termin.id"
          >
            <RouterLink
              :to="`/projects/${route.params.id}/termin/${termin.id}`"
              class="w-full flex items-center gap-[6px] text-sm p-[6px] rounded-primary-radius bg-transparent text-primary-text-color font-medium tracking-[0.01rem] cursor-pointer hover:bg-white duration-300"
            >
              <span>{{
                termin.end
                  ? new Date(termin.end).toLocaleDateString("DE")
                  : "Not scheduled"
              }}</span>
              <ColoredDot
                :status-name="termin.status.name"
                :status-id="termin.status.id"
                :is-termin="true"
              />
            </RouterLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div
    v-else-if="props.phasePage"
    class="w-[220px] h-full flex flex-col overflow-y-auto"
  >
    <!-- Phases -->
    <div
      v-for="(phase, index) in props.phasePage?.phases"
      :key="phase.phaseId"
      class="w-full h-fit rounded-[4px] m-[2px] mt-[1px] ml-[1px] p-3 bg-[#E5E4E2]"
      :class="
        index === props.phasePage?.phases.length - 1 ? 'h-full' : 'h-auto'
      "
    >
      <h4
        class="text-base leading-[26px] tracking-[0.01rem] uppercase font-medium text-[#51687A]"
      >
        {{ phase?.phase }}
      </h4>
      <!-- Menu items -->
      <div class="w-full flex flex-col items-start mt-2 h-fit">
        <!-- Review -->
        <RouterLink
          :to="`/projects/${route.params.id}/phase/${phase.phaseId}`"
          class="w-full cursor-pointer h-fit"
        >
          <div
            class="w-fit flex items-center flex-wrap gap-[2px] p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
          >
            <img
              v-if="selectedPhaseId === phase.phaseId"
              src="@/assets/caret_up.svg"
              alt="Eye"
            />
            <img v-else src="@/assets/eye-icon.svg" alt="" />
            <h5
              class="text-[#393840] font-medium text-sm tracking-[0.01rem] pr-1"
            >
              {{ t("global.review") }}
            </h5>
            <div class="flex items-center">
              <span
                v-if="[3, 4, 5].includes(phase.phaseStatus.id)"
                class="text-primary-text-color font-medium text-[13px] tracking-[0.01rem] px-[6px] py-[2px] rounded-primary-radius"
                :style="{
                  backgroundColor: `${getPhaseStatusColor(
                    phase.phaseStatus.id
                  )}`,
                }"
                >{{ phase.phaseStatus.name }}</span
              >
              <ColoredDot
                v-else
                :status-name="phase.phaseStatus.name"
                :status-id="phase.phaseStatus.id"
                :is-termin="false"
              />
            </div>
          </div>
          <ul class="w-full h-fit" v-if="selectedPhaseId === phase.phaseId">
            <div
              class="w-full flex items-center justify-center my-4"
              v-if="props.isLoading"
            >
              <SpinnerLoader />
            </div>
            <div v-else class="pl-5 h-fit">
              <li
                class="w-full flex flex-col gap-2 py-[2px] relative before:absolute before:w-[2px] before:bg-[#D3D1CD] before:h-full before:-ml-[6px]"
                v-for="step in props.phasePage?.steps"
                :key="step.id"
              >
                <RouterLink
                  :to="`/projects/${projectId}/phase/${phase.phaseId}/#step-${step.id}`"
                  class="break-words w-full relative text-sm p-[6px] rounded-primary-radius bg-transparent text-primary-text-color font-medium tracking-[0.01rem] cursor-pointer hover:bg-[#D3D1CD] duration-300"
                  :class="`${
                    route.hash &&
                    (route.hash.replace('#step-', '') == step.id ||
                      route.hash.replace('#scroll-step-', '') == step.id)
                      ? 'bg-white before:absolute before:w-[2px] before:top-0 before:-left-[6px] before:bg-[#51687A] before:h-full'
                      : 'bg-transparent'
                  }`"
                >
                  {{ step.name }}
                </RouterLink>
              </li>
            </div>
          </ul>
        </RouterLink>
        <!-- Appointment -->
        <div class="w-full cursor-pointer">
          <div
            @click="toggleAppointmentsDropdown(phase)"
            class="w-full flex items-center gap-1 p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
          >
            <img
              v-if="selectedAppointment === phase"
              src="@/assets/caret_up.svg"
              alt="Caret"
            />
            <img
              v-else
              src="@/assets/phase-menu-calendar-icon.svg"
              alt="Calendar"
            />
            <h5 class="text-[#393840] font-medium text-sm tracking-[0.01rem]">
              {{ t("global.termins") }}
            </h5>
          </div>
          <ul v-if="selectedAppointment === phase" class="pl-5">
            <li
              class="w-full flex flex-col gap-2 relative before:absolute before:w-[2px] before:bg-[#D3D1CD] before:h-full before:-ml-[6px]"
              v-for="termin in phase.termins"
              :key="termin.id"
            >
              <RouterLink
                :to="`/projects/${route.params.id}/termin/${termin.id}`"
                class="w-full flex items-center gap-[6px] text-sm p-[6px] rounded-primary-radius bg-transparent text-primary-text-color font-medium tracking-[0.01rem] cursor-pointer hover:bg-white duration-300"
              >
                <span>{{
                  termin.end
                    ? new Date(termin.end).toLocaleDateString("DE")
                    : "Not scheduled"
                }}</span>
                <ColoredDot
                  :status-name="termin.termin_status.name"
                  :status-id="termin.termin_status.id"
                  :is-termin="true"
                />
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else-if="props.appointmentPage"
    class="w-[220px] h-full overflow-y-auto flex flex-col"
  >
    <!-- Phases -->
    <div
      v-for="(phase, index) in props.appointmentPage"
      :key="phase.id"
      class="w-full h-fit rounded-[4px] m-[2px] mt-[1px] ml-[1px] py-4 px-3 bg-[#E5E4E2]"
      :class="index === props.appointmentPage?.length - 1 ? 'h-full' : 'h-auto'"
    >
      <h4
        class="flex text-base leading-[26px] tracking-[0.01rem] uppercase font-medium text-[#51687A]"
      >
        {{ phase?.phase.name }}
        <span></span>
      </h4>
      <!-- Menu items -->
      <div class="w-full flex flex-col items-start mt-2">
        <!-- Review -->
        <RouterLink
          :to="`/projects/${projectId}/phase/${phase.phase.id}`"
          class="w-full cursor-pointer"
        >
          <div
            class="w-full flex flex-wrap items-center gap-[2px] p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
          >
            <img src="@/assets/eye-icon.svg" alt="Eye" />
            <h5
              class="text-[#393840] font-medium text-sm tracking-[0.01rem] pr-1"
            >
              {{ t("global.review") }}
            </h5>
            <div class="flex items-center">
              <span
                v-if="[3, 4, 5].includes(phase.status.id)"
                class="text-primary-text-color font-medium text-[13px] tracking-[0.01rem] px-[6px] py-[2px] rounded-primary-radius"
                :style="{
                  backgroundColor: `${getPhaseStatusColor(phase.status.id)}`,
                }"
                >{{ phase.status.name }}</span
              >
              <ColoredDot
                v-else
                :status-name="phase.status.name"
                :status-id="phase.status.id"
                :is-termin="false"
              />
            </div>
          </div>
          <ul></ul>
        </RouterLink>
        <!-- Appointment -->
        <div class="w-full cursor-pointer">
          <div
            class="w-full flex items-center gap-1 p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
            @click="toggleAppointmentsDropdown(phase.phase.id)"
          >
            <img
              v-if="appointmentsDropdownOpened.includes(phase.phase.id)"
              src="@/assets/caret_up.svg"
              alt="Caret"
            />
            <img
              v-else
              src="@/assets/phase-menu-calendar-icon.svg"
              alt="Calendar"
            />
            <h5
              class="text-[#393840] font-medium text-[14px] tracking-[0.01rem]"
            >
              {{ t("global.termins") }}
            </h5>
          </div>
          <ul
            class="mt-1"
            v-if="appointmentsDropdownOpened.includes(phase.phase.id)"
          >
            <div
              class="w-full flex items-center justify-center my-4"
              v-if="props.isLoading"
            >
              <SpinnerLoader />
            </div>
            <div v-else class="pl-5">
              <li
                class="w-full flex flex-col gap-2 py-[2px] relative before:absolute before:w-[2px] before:bg-[#D3D1CD] before:h-full before:-ml-[6px]"
                v-for="termin in phase.termins"
                :key="termin.id"
              >
                <RouterLink
                  :to="`/projects/${route.params.id}/termin/${termin.id}`"
                  class="w-full flex items-center gap-[6px] text-sm p-[6px] rounded-primary-radius bg-transparent text-primary-text-color font-medium tracking-[0.01rem] cursor-pointer hover:bg-[#D3D1CD] duration-300"
                  :class="
                    route.params.terminId === termin.id
                      ? 'bg-white before:absolute before:w-[2px] before:top-0 before:left-[-6px] before:bg-[#51687A] before:h-full'
                      : 'bg-transparent'
                  "
                >
                  <span>{{
                    termin.end
                      ? new Date(termin.end).toLocaleDateString("DE")
                      : "Not scheduled"
                  }}</span>
                  <ColoredDot
                    :status-name="termin.status.name"
                    :status-id="termin.status.id"
                    :is-termin="true"
                  />
                </RouterLink>
              </li>
            </div>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
